import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Minus,
  Mail,
  MapPin,
  Train,
  Wrench,
  GraduationCap,
  Building2,
  ShieldCheck,
  ArrowRight,
  Search,
} from "lucide-react";
import { useSeo } from "../lib/useSeo";

const SITE_URL = "https://railwaycareers.co.uk";

const CATEGORIES = [
  { id: "train-driver", label: "Train Driver", icon: Train },
  { id: "rail-jobs", label: "Rail Jobs", icon: Search },
  { id: "employers", label: "TOCs & Employers", icon: Building2 },
  { id: "engineering", label: "Engineering", icon: Wrench },
  { id: "apprenticeships", label: "Apprenticeships", icon: GraduationCap },
  { id: "medical-safety", label: "Medical & Safety", icon: ShieldCheck },
];

const FAQS = [
  {
    category: "train-driver",
    q: "How do I become a train driver in the UK?",
    a: "You normally apply directly to a Train Operating Company, freight operator or other rail employer when trainee train driver vacancies are advertised. The process usually includes an online application, screening questions, psychometric assessments, structured interviews, medical checks, references and a paid training programme. Requirements vary by operator, so always check the individual vacancy.",
  },
  {
    category: "train-driver",
    q: "Do I need railway experience to become a train driver?",
    a: "Not always. Many trainee train driver vacancies are open to people from outside the railway. Applicants usually need to demonstrate strong safety awareness, concentration, communication, reliability, decision-making and motivation for shift-based work.",
  },
  {
    category: "train-driver",
    q: "How much does a UK train driver earn?",
    a: "Trainee train driver salaries are commonly lower during training and increase once qualified. Qualified driver salaries vary significantly by operator, location, traction, route knowledge, grade, agreements and overtime. Treat published salary ranges as indicative and always check the employer’s current vacancy advert.",
  },
  {
    category: "train-driver",
    q: "How long does train driver training take?",
    a: "Training times vary by employer, route, traction, training capacity and individual progress. Many trainee driver programmes take around 12 to 18 months, but this is not guaranteed and should be treated as an indicative range.",
  },
  {
    category: "train-driver",
    q: "What assessments do trainee train driver applicants take?",
    a: "Train driver recruitment usually includes psychometric assessments designed to test areas such as concentration, attention, memory, reaction, rules-based decision making and the ability to remain consistent under pressure. Operators may use different assessment formats, so preparation should focus on core skills and understanding the process rather than memorising specific questions.",
  },
  {
    category: "train-driver",
    q: "Do I need to be 21 to become a train driver?",
    a: "Age requirements vary by operator, vacancy and regulatory position. Some roles may have minimum age requirements linked to safety-critical work, training timescales or licensing. Always check the individual vacancy before applying.",
  },

  {
    category: "rail-jobs",
    q: "Do I need rail experience to apply for railway jobs?",
    a: "No, not for every role. Many rail employers recruit people from outside the industry into customer service, conductor, station, trainee driver, depot, apprenticeship and graduate roles. Engineering and specialist technical roles may require qualifications or relevant experience, but there are also entry-level and apprenticeship pathways.",
  },
  {
    category: "rail-jobs",
    q: "How do I find railway jobs near me?",
    a: "Use the Railway Careers vacancy search and depot finder to search by role, employer, keyword and location. For shift-based railway roles, commute time matters because early starts, late finishes and night turns can make some depots impractical even if they appear close on a map.",
  },
  {
    category: "rail-jobs",
    q: "What are the best entry-level railway jobs?",
    a: "Common entry routes include customer service assistant, gateline, station roles, conductor, depot roles, apprenticeships, graduate schemes and some trainee operational roles. The best option depends on your location, qualifications, experience, travel availability and long-term career aim.",
  },
  {
    category: "rail-jobs",
    q: "Can customer service jobs lead to other railway careers?",
    a: "Yes. Station and customer service roles can help you build railway knowledge, safety awareness, passenger-handling experience and internal credibility. They do not guarantee progression, but they can strengthen future applications for operational or supervisory roles.",
  },

  {
    category: "employers",
    q: "What is the difference between a TOC, FOC and Network Rail?",
    a: "A TOC is a Train Operating Company that runs passenger services. A FOC is a Freight Operating Company that runs freight services. Network Rail manages much of the railway infrastructure, including track, signalling and structures. Rail contractors and rolling stock companies also employ people across engineering, maintenance, project and operational roles.",
  },
  {
    category: "employers",
    q: "Do I apply through Railway Careers or the employer?",
    a: "Railway Careers helps you find and understand opportunities. Where available, vacancy links should take you to the original employer or operator application page. You should always complete the official employer application process and follow the instructions in the vacancy advert.",
  },
  {
    category: "employers",
    q: "Which rail employers recruit in the UK?",
    a: "UK rail recruitment includes passenger operators, freight operators, infrastructure managers, rolling stock companies, local transport bodies and major contractors. Examples include Train Operating Companies, Network Rail, TfL, freight businesses, maintenance providers and engineering contractors.",
  },

  {
    category: "engineering",
    q: "How do I get into rail engineering?",
    a: "Routes into rail engineering include apprenticeships, graduate schemes, direct entry from mechanical or electrical engineering, construction, utilities, telecoms, manufacturing, transport or armed forces technical roles. Requirements vary by discipline and employer.",
  },
  {
    category: "engineering",
    q: "What railway engineering roles are available?",
    a: "Rail engineering roles include rolling stock maintenance, traction and rolling stock engineering, signalling, telecoms, track, structures, electrification, overhead line equipment, project engineering, asset management and reliability engineering.",
  },
  {
    category: "engineering",
    q: "Do rail engineering jobs require PTS?",
    a: "Some on-track or infrastructure roles may require Personal Track Safety or other safety competences. Depot-based rolling stock roles may have different local safety requirements. The employer will normally specify required competences and arrange role-specific training where appropriate.",
  },

  {
    category: "apprenticeships",
    q: "Are there UK rail apprenticeships?",
    a: "Yes. Rail apprenticeships exist across engineering, signalling, telecoms, customer service, operations, business support, digital, project management and infrastructure. Entry requirements, pay and duration vary by employer, apprenticeship level and location.",
  },
  {
    category: "apprenticeships",
    q: "Can I apply for a railway apprenticeship at 16?",
    a: "Some apprenticeships may be open from age 16, while others may have higher age, education, safety or travel requirements. Always check the specific apprenticeship advert before applying.",
  },
  {
    category: "apprenticeships",
    q: "Are railway apprenticeships paid?",
    a: "Yes. Apprenticeships are paid roles. Pay varies by employer, programme, region and apprenticeship level, and may increase as you progress through the scheme.",
  },

  {
    category: "medical-safety",
    q: "What is a Sentinel card?",
    a: "Sentinel is a rail industry system used to hold safety-critical competence information for people working on or near railway infrastructure. Not every rail job needs Sentinel, but many infrastructure and on-track roles require specific competences recorded through the system.",
  },
  {
    category: "medical-safety",
    q: "What is Personal Track Safety?",
    a: "Personal Track Safety, often shortened to PTS, is a competence for people who need to work on or near railway lines. It is usually arranged through an employer or sponsor and is not normally something applicants should pay for speculatively without a clear route into sponsored rail work.",
  },
  {
    category: "medical-safety",
    q: "Can I work for the railway with a criminal record?",
    a: "This depends on the role, employer, offence, whether the conviction is spent or unspent, and the safety-critical nature of the job. Always answer application questions honestly and follow the employer’s disclosure instructions.",
  },
  {
    category: "medical-safety",
    q: "What medical checks are required for railway jobs?",
    a: "Medical requirements depend on the role. Safety-critical jobs may include eyesight, hearing, colour vision, general health, drug and alcohol screening and fitness-for-duty checks. The employer will confirm the required medical standard during recruitment.",
  },

  {
    category: "rail-jobs",
    q: "Is Railway Careers UK free for jobseekers?",
    a: "Yes. Railway Careers is free for jobseekers to search vacancies, read role guides, use career tools and access rail recruitment information. Optional employer services or future premium support may be separate from the free jobseeker experience.",
  },
];

export default function FAQs() {
  const [openIdx, setOpenIdx] = useState(0);
  const [activeCategory, setActiveCategory] = useState("all");

  const visibleFaqs =
    activeCategory === "all"
      ? FAQS
      : FAQS.filter((faq) => faq.category === activeCategory);

  useSeo({
    title: "UK Railway Careers FAQ | Train Driver Jobs, Rail Apprenticeships & TOCs",
    description:
      "Answers to common UK railway career questions, including train driver jobs, rail apprenticeships, TOCs, Network Rail, salaries, medicals, PTS and rail recruitment.",
    canonical: "/faqs",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.a,
        },
      })),
    },
  });

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${SITE_URL}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Railway Careers FAQ",
        item: `${SITE_URL}/faqs`,
      },
    ],
  };

  return (
    <div className="bg-cream min-h-screen">
      <main className="max-w-5xl mx-auto px-6 md:px-12 pt-14 pb-20">
        <section>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-ink">
            FAQs · UK Rail Recruitment
          </div>

          <h1 className="font-display font-extrabold text-ink text-4xl md:text-6xl tracking-[-0.025em] mt-3 leading-[1.05]">
            UK railway careers, train driver jobs and{" "}
            <span className="text-amber-700">rail recruitment FAQs</span>.
          </h1>

          <p className="mt-5 text-muted-foreground text-lg leading-relaxed max-w-3xl">
            Clear answers about railway jobs in the UK, including trainee train
            driver recruitment, conductor roles, rail apprenticeships, engineering
            careers, TOCs, Network Rail, PTS, medicals, salaries and vacancy searches.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/vacancies"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-bone font-semibold hover:bg-lime-600 hover:text-ink transition-colors"
            >
              Search rail vacancies <ArrowRight className="w-4 h-4" />
            </Link>

            <Link
              to="/depot-finder"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border text-ink font-semibold hover:bg-paper transition-colors"
            >
              <MapPin className="w-4 h-4" /> Find depots near me
            </Link>
          </div>
        </section>

        <section className="mt-12 grid md:grid-cols-3 gap-4">
          <Link
            to="/careers/train-driver"
            className="bg-card border border-border rounded-lg p-5 hover:bg-paper transition-colors"
          >
            <Train className="w-6 h-6 text-amber-700" />
            <h2 className="font-display font-bold text-ink text-xl mt-3">
              Train Driver Guide
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              Learn about trainee driver recruitment, training, assessments and salary.
            </p>
          </Link>

          <Link
            to="/careers/apprentice"
            className="bg-card border border-border rounded-lg p-5 hover:bg-paper transition-colors"
          >
            <GraduationCap className="w-6 h-6 text-amber-700" />
            <h2 className="font-display font-bold text-ink text-xl mt-3">
              Rail Apprenticeships
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              Explore apprenticeship routes across engineering, operations and support.
            </p>
          </Link>

          <Link
            to="/careers/engineering"
            className="bg-card border border-border rounded-lg p-5 hover:bg-paper transition-colors"
          >
            <Wrench className="w-6 h-6 text-amber-700" />
            <h2 className="font-display font-bold text-ink text-xl mt-3">
              Rail Engineering
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              Understand signalling, track, rolling stock and infrastructure careers.
            </p>
          </Link>
        </section>

        <section className="mt-12 bg-card border border-border rounded-lg p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink font-semibold">
            Browse questions by topic
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setActiveCategory("all");
                setOpenIdx(0);
              }}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                activeCategory === "all"
                  ? "bg-navy text-bone border-navy"
                  : "bg-cream text-ink border-border hover:bg-paper"
              }`}
            >
              All FAQs
            </button>

            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const active = activeCategory === cat.id;

              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => {
                    setActiveCategory(cat.id);
                    setOpenIdx(0);
                  }}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    active
                      ? "bg-navy text-bone border-navy"
                      : "bg-cream text-ink border-border hover:bg-paper"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </section>

        <section className="mt-8 space-y-3" data-testid="faqs-list">
          {visibleFaqs.map((f, i) => {
            const open = i === openIdx;

            return (
              <article
                key={`${f.category}-${i}`}
                className={`border rounded-lg transition-colors ${
                  open ? "border-amber bg-paper" : "border-border bg-card"
                }`}
              >
                <h2 className="m-0">
                  <button
                    type="button"
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left"
                    data-testid={`faq-toggle-${i}`}
                    aria-expanded={open}
                  >
                    <span className="font-display font-bold text-ink text-[17px] md:text-lg leading-snug">
                      {f.q}
                    </span>

                    {open ? (
                      <Minus className="w-5 h-5 text-amber-700 shrink-0" />
                    ) : (
                      <Plus className="w-5 h-5 text-muted-foreground shrink-0" />
                    )}
                  </button>
                </h2>

                {open && (
                  <div className="px-5 pb-5 -mt-1 text-[15px] text-muted-foreground leading-relaxed">
                    {f.a}
                  </div>
                )}
              </article>
            );
          })}
        </section>

        <section className="mt-14 rounded-lg bg-navy text-bone p-7">
          <div className="text-[11px] uppercase tracking-[0.18em] text-amber font-semibold">
            Next step
          </div>

          <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight mt-2">
            Ready to search current UK railway vacancies?
          </h2>

          <p className="mt-3 text-bone/70 max-w-3xl">
            Search live rail jobs by role, employer, keyword and location, or use the
            depot finder to identify practical railway locations near your postcode.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/vacancies"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber text-ink font-semibold hover:bg-lime-600 transition-colors"
            >
              Search vacancies <ArrowRight className="w-4 h-4" />
            </Link>

            <Link
              to="/careers"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-bone/20 text-bone font-semibold hover:bg-bone/10 transition-colors"
            >
              Browse role guides
            </Link>
          </div>
        </section>

        <section className="mt-14 rounded-lg bg-card border border-border p-7">
          <h2 className="font-display font-extrabold text-ink text-2xl md:text-3xl tracking-tight">
            Still have a question?
          </h2>

          <p className="mt-2 text-muted-foreground">
            Send us your question. We use common questions to improve this FAQ and
            make UK railway recruitment easier to understand.
          </p>

          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href="mailto:hello@railwaycareers.co.uk"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy text-bone font-semibold hover:bg-lime-600 hover:text-ink transition-colors"
            >
              <Mail className="w-4 h-4" /> hello@railwaycareers.co.uk
            </a>

            <Link
              to="/depot-finder"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-border text-ink font-semibold hover:bg-paper transition-colors"
            >
              <MapPin className="w-4 h-4" /> Find vacancies near me
            </Link>
          </div>
        </section>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />
    </div>
  );
}